import { PublicClientApplication } from "@azure/msal-browser";
import {
  clearClientAuthStorage,
  clearSessionEnded,
  isOnGoodbyePage,
  isSessionEnded,
} from "./sessionEnded";
import {
  captureReturnUrl,
  restoreReturnUrlAfterAuth,
} from "./sessionReturnUrl";

// CRA inlines only static process.env.REACT_APP_* accesses — avoid destructuring.
const CLIENT_ID = process.env.REACT_APP_ENTRA_CLIENT_ID;
const TENANT_ID = process.env.REACT_APP_ENTRA_TENANT_ID;
const MSAL_REDIRECT_URI = process.env.REACT_APP_MSAL_REDIRECT_URI;

if (!CLIENT_ID || !TENANT_ID) {
  throw new Error(
    "Missing REACT_APP_ENTRA_CLIENT_ID and/or REACT_APP_ENTRA_TENANT_ID. " +
      "Copy ctpo_web/sample.env to ctpo_web/.env (local) or set the GitHub Actions secrets, then rebuild.",
  );
}

// Prefer REACT_APP_MSAL_REDIRECT_URI when set (e.g. …/redirect.html after IT registers it).
// Default stays on the historically registered SPA path so login works before Entra is updated.
// MSAL Browser v5 redirect-bridge page: public/redirect.html — switch via env once Entra lists it.
const defaultRedirectUri = `${window.location.origin}/ctpo/study-plans/`;
const msalConfig = {
  auth: {
    clientId: CLIENT_ID,
    authority: `https://login.microsoftonline.com/${TENANT_ID}`,
    redirectUri: MSAL_REDIRECT_URI || defaultRedirectUri,
    postLogoutRedirectUri: window.location.origin + "/goodbye",
  },
  cache: {
    cacheLocation: "sessionStorage",
  },
};

export const loginRequest = {
  scopes: ["User.Read", "GroupMember.Read.All"],
};

export const tokenRequest = {
  scopes: ["User.Read", "GroupMember.Read.All"],
};

export const msalInstance = new PublicClientApplication(msalConfig);

export function getLoginRequest() {
  return { ...loginRequest };
}

export function getTokenRequest() {
  return { ...tokenRequest };
}

let loginRedirectStarted = false;

export function isLoginRedirectStarted() {
  return loginRedirectStarted;
}

function findValidAccount() {
  return msalInstance.getAllAccounts().find(
    (a) =>
      (a.environment === "login.microsoftonline.com" ||
        a.environment === "login.windows.net") &&
      a.tenantId === TENANT_ID,
  );
}

function audienceIsForeign(claims) {
  const aud = claims?.aud;
  if (aud == null || aud === "") {
    return false;
  }
  if (Array.isArray(aud)) {
    return aud.length > 0 && !aud.includes(CLIENT_ID);
  }
  return aud !== CLIENT_ID;
}

function completeLogin(account) {
  msalInstance.setActiveAccount(account);
  clearSessionEnded();
  restoreReturnUrlAfterAuth();
}

function goToGoodbye() {
  if (isOnGoodbyePage()) {
    return;
  }
  window.location.replace(`${window.location.origin}/goodbye`);
}

/**
 * One Entra hop. No prompt:login — Microsoft SSO completes if My Apps is signed in;
 * otherwise the normal Microsoft sign-in page appears. Does not log Microsoft out.
 */
export async function startSsoLogin() {
  if (loginRedirectStarted) {
    return;
  }
  loginRedirectStarted = true;
  captureReturnUrl();
  try {
    await msalInstance.loginRedirect(getLoginRequest());
  } catch (error) {
    loginRedirectStarted = false;
    throw error;
  }
}

export const msalReady = msalInstance.initialize().then(async () => {
  let response = null;
  try {
    response = await msalInstance.handleRedirectPromise();
  } catch (error) {
    console.debug("handleRedirectPromise failed", error);
    goToGoodbye();
    return;
  }

  if (response?.account && !audienceIsForeign(response.idTokenClaims)) {
    completeLogin(response.account);
    return;
  }

  if (response && audienceIsForeign(response.idTokenClaims)) {
    clearClientAuthStorage();
    return;
  }

  // Stay on Goodbye until the user clicks Sign In.
  if (isOnGoodbyePage()) {
    return;
  }

  const existing = findValidAccount();
  if (existing && !isSessionEnded()) {
    msalInstance.setActiveAccount(existing);
    return;
  }

  if (existing && isSessionEnded()) {
    clearClientAuthStorage();
  }

  try {
    await startSsoLogin();
  } catch (error) {
    console.debug("SSO login redirect failed", error);
    if (error?.errorCode === "interaction_in_progress") {
      return;
    }
    goToGoodbye();
  }
});
